'use client';

import { useAmplitude } from '@/providers/AmplitudeProvider';
import { useAuth } from '@/lib/auth/auth-context';
import { useCallback, useEffect } from 'react';

export interface UserProperties {
  role?: string;
  subscriptionStatus?: string;
  businessType?: string;
  teamSize?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface OrderEventProperties extends Record<string, unknown> {
  orderId?: string;
  orderValue?: number;
  itemCount?: number;
  orderType?: string;
  paymentMethod?: string;
}

export interface ProductEventProperties extends Record<string, unknown> {
  productId?: string;
  productName?: string;
  category?: string;
  price?: number;
}

export interface EventEventProperties extends Record<string, unknown> {
  eventId?: string;
  eventType?: string;
  attendeeCount?: number;
  eventDate?: string;
}

export interface PageViewProperties extends Record<string, unknown> {
  page: string;
  section?: string;
  userId?: string;
}

export const useAnalytics = () => {
  const { track, identify, setUserId } = useAmplitude();
  const { user } = useAuth();

  // Initialize user tracking when user logs in
  useEffect(() => {
    if (user) {
      setUserId(user.id);
      identify(user.id, {
        email: user.email,
        role: user.role,
        firstName: user.firstName,
        lastName: user.lastName,
        vendorId: user.vendorId,
      });
    }
  }, [user, setUserId, identify]);

  // Authentication Events
  const trackSignUp = useCallback((method: 'email' | 'google' = 'email') => {
    track('User Signed Up', { method });
  }, [track]);

  const trackSignIn = useCallback((method: 'email' | 'google' = 'email') => {
    track('User Signed In', { method });
  }, [track]);

  const trackSignOut = useCallback(() => {
    track('User Signed Out');
  }, [track]);

  // Order Events
  const trackOrderCreated = useCallback((properties: OrderEventProperties) => {
    track('Order Created', properties);
  }, [track]);

  const trackOrderUpdated = useCallback((properties: OrderEventProperties) => {
    track('Order Updated', properties);
  }, [track]);

  const trackOrderDeleted = useCallback((properties: OrderEventProperties) => {
    track('Order Deleted', properties);
  }, [track]);

  const trackOrderPaid = useCallback((properties: OrderEventProperties) => {
    track('Order Paid', properties);
  }, [track]);

  // Product Events
  const trackProductCreated = useCallback((properties: ProductEventProperties) => {
    track('Product Created', properties);
  }, [track]);

  const trackProductUpdated = useCallback((properties: ProductEventProperties) => {
    track('Product Updated', properties);
  }, [track]);

  const trackProductDeleted = useCallback((properties: ProductEventProperties) => {
    track('Product Deleted', properties);
  }, [track]);

  // Event Management Events
  const trackEventCreated = useCallback((properties: EventEventProperties) => {
    track('Event Created', properties);
  }, [track]);

  const trackEventUpdated = useCallback((properties: EventEventProperties) => {
    track('Event Updated', properties);
  }, [track]);

  const trackInventoryAllocated = useCallback((properties: { eventId: string; itemCount: number }) => {
    track('Inventory Allocated', properties);
  }, [track]);

  // Customer Events
  const trackCustomerCreated = useCallback((properties: { customerId: string; source?: string }) => {
    track('Customer Created', properties);
  }, [track]);

  const trackCustomerConverted = useCallback((properties: { customerId: string; fromLead: boolean }) => {
    track('Customer Converted', properties);
  }, [track]);

  // Payment Events
  const trackPaymentInitiated = useCallback((properties: { amount: number; method: string; orderId?: string }) => {
    track('Payment Initiated', properties);
  }, [track]);

  const trackPaymentCompleted = useCallback((properties: { amount: number; method: string; orderId?: string }) => {
    track('Payment Completed', properties);
  }, [track]);

  const trackPaymentFailed = useCallback((properties: { amount: number; method: string; error?: string }) => {
    track('Payment Failed', properties);
  }, [track]);

  // Navigation Events
  const trackPageView = useCallback((properties: PageViewProperties) => {
    track('Page Viewed', properties);
  }, [track]);

  const trackFeatureUsed = useCallback((feature: string, properties?: Record<string, unknown>) => {
    track('Feature Used', { feature, ...properties });
  }, [track]);

  // WhatsApp Events
  const trackWhatsAppMessageSent = useCallback((properties: { messageType: string; recipientCount: number }) => {
    track('WhatsApp Message Sent', properties);
  }, [track]);

  // Menu Website Events
  const trackMenuWebsiteCreated = useCallback((properties: { vendorId: string; code: string }) => {
    track('MenuMate Link Created', properties);
  }, [track]);

  const trackMenuWebsiteVisited = useCallback((properties: { code: string; source?: string }) => {
    track('MenuMate Link Visited', properties);
  }, [track]);

  // Error Events
  const trackError = useCallback((error: string, context?: Record<string, unknown>) => {
    track('Error Occurred', { error, ...context });
  }, [track]);

  return {
    // Auth
    trackSignUp,
    trackSignIn,
    trackSignOut,
    
    // Orders
    trackOrderCreated,
    trackOrderUpdated,
    trackOrderDeleted,
    trackOrderPaid,
    
    // Products
    trackProductCreated,
    trackProductUpdated,
    trackProductDeleted,
    
    // Events
    trackEventCreated,
    trackEventUpdated,
    trackInventoryAllocated,
    
    // Customers
    trackCustomerCreated,
    trackCustomerConverted,
    
    // Payments
    trackPaymentInitiated,
    trackPaymentCompleted,
    trackPaymentFailed,
    
    // Navigation
    trackPageView,
    trackFeatureUsed,
    
    // WhatsApp
    trackWhatsAppMessageSent,
    
    // Menu Website
    trackMenuWebsiteCreated,
    trackMenuWebsiteVisited,
    
    // Errors
    trackError,
    
    // Raw tracking function
    track,
  };
};